import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

const CenterNavLink = ({ to, children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = (e) => {
    // If it's an in-page anchor (starts with #), smooth-scroll when already on home,
    // otherwise navigate to the home path with hash so the browser will land there.
    if (to && to.startsWith('#')) {
      e.preventDefault();
      if (location.pathname === '/' || location.pathname === '/about') {
        const id = to.slice(1);
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          // fallback: navigate to home with hash
          navigate('/' + to);
        }
      } else {
        navigate('/' + to);
      }
    } else {
      // regular route
      navigate(to);
    }
  };

  // determine active state
  const base = 'text-sm px-4 py-2 rounded-lg transition-all duration-200';
  const activeClass = 'bg-primary-600 text-white shadow';
  const inactiveClass = 'text-gray-700 hover:text-primary-600';

  // Only "Home" should appear active on the landing page.
  let isActive = false;
  if (to === '/') {
    isActive = location.pathname === '/' || location.pathname === '/about';
  } else if (to && to.startsWith('#')) {
    // In-page anchor links never get the active style
    isActive = false;
  } else {
    isActive = location.pathname === to;
  }

  return (
    <button onClick={handleClick} className={`${base} ${isActive ? activeClass : inactiveClass}`}>
      {children}
    </button>
  );
};

export default function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="w-full mt-5">
      <div className="w-full mx-auto px-6 lg:px-10 xl:px-16 2xl:px-24">
        <div className="flex items-center justify-between py-4">

          {/* LEFT: LOGO */}
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3" onClick={closeMobileMenu}>
              <img
                src="/bidz-logo.svg"
                alt="BidZ"
                className="h-8 md:h-10 object-contain"
              />
            </Link>
          </div>

          {/* CENTER: NAV LINKS - Hidden on mobile/tablet, shown on lg+ */}
          <div className="hidden lg:flex items-center justify-center gap-2">
            <CenterNavLink to="/">Home</CenterNavLink>
            <CenterNavLink to="#about">About</CenterNavLink>
            <CenterNavLink to="#offerings">Offerings</CenterNavLink>
            <CenterNavLink to="#features">Features</CenterNavLink>
            <CenterNavLink to="#pricing">Pricing</CenterNavLink>
            <CenterNavLink to="#reviews">Reviews</CenterNavLink>
            <CenterNavLink to="#contact">Contact</CenterNavLink>
          </div>

          {/* RIGHT: LOGIN BUTTON & MOBILE MENU */}
          <div className="flex items-center gap-4">
            {/* Login Button - desktop only (lg+). On smaller screens it's only inside the side menu. */}
            <Link
              to="/login"
              state={{ from: location.pathname + location.search + location.hash }} // <-- pass current location
              className="hidden lg:inline-block bg-gradient-to-r from-primary-blue to-primary-500 text-white px-14 py-2 rounded-lg text-sm shadow-md hover:opacity-95 transition-opacity"
            >
              Login
            </Link>

            {/* Mobile Menu Toggle Button - Shown on screens < lg */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 text-gray-700 hover:text-primary-blue transition-colors"
              aria-label="Toggle menu"
            >
              <img
                src="/navebar-side-icon.svg"
                alt="Menu"
                className="w-6 h-6"
              />
            </button>
          </div>
        </div>

        {/* MOBILE MENU - Shown on screens < lg */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-50 bg-white">
            {/* Mobile Menu Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <Link to="/" className="flex items-center gap-3" onClick={closeMobileMenu}>
                <img
                  src="/bidz-logo.svg"
                  alt="BidZ"
                  className="h-8 object-contain"
                />
              </Link>
              <button
                onClick={closeMobileMenu}
                className="p-2 text-gray-700 hover:text-primary-blue transition-colors"
                aria-label="Close menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Mobile Menu Links */}
            <div className="px-6 py-8 space-y-4">
              <MobileNavLink to="/" onClick={closeMobileMenu}>Home</MobileNavLink>
              <MobileNavLink to="#about" onClick={closeMobileMenu}>About</MobileNavLink>
              <MobileNavLink to="#offerings" onClick={closeMobileMenu}>Offerings</MobileNavLink>
              <MobileNavLink to="#features" onClick={closeMobileMenu}>Features</MobileNavLink>
              <MobileNavLink to="#pricing" onClick={closeMobileMenu}>Pricing</MobileNavLink>
              <MobileNavLink to="#reviews" onClick={closeMobileMenu}>Reviews</MobileNavLink>
              <MobileNavLink to="#contact" onClick={closeMobileMenu}>Contact</MobileNavLink>
              
              {/* Mobile Login Button */}
              <div className="pt-6 border-t border-gray-200">
                <Link
                  to="/login"
                   state={{ from: location.pathname + location.search + location.hash }} // <-- pass current location (mobile)
                  onClick={closeMobileMenu}
                  className="block w-full text-center bg-gradient-to-r from-primary-blue to-primary-500 text-white px-6 py-3 rounded-lg text-sm shadow-md hover:opacity-95 transition-opacity"
                >
                  Login
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

// Mobile Nav Link Component
const MobileNavLink = ({ to, children, onClick }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = (e) => {
    if (to && to.startsWith('#')) {
      e.preventDefault();
      if (location.pathname === '/' || location.pathname === '/about') {
        const id = to.slice(1);
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          navigate('/' + to);
        }
      } else {
        navigate('/' + to);
      }
    } else {
      navigate(to);
    }
    onClick();
  };

  return (
    <button
      onClick={handleClick}
      className="block w-full text-left text-gray-700 hover:text-primary-blue py-3 text-base font-medium transition-colors"
    >
      {children}
    </button>
  );
};
